import { useCallback, useEffect, useState } from 'react';
import api from '../../api/endpoints';
import { fileUrl, uploadDirect } from '../../api/client';
import PageHeader from '../../components/PageHeader';
import { Button, Chip, Empty, Input, Loading } from '../../components/Ui';
import { Reveal, stagger } from '../../components/Motion';
import { startOnlinePayment } from '../../services/payment';
import { useToast } from '../../context/ToastContext';
import { colors } from '../../theme';
import { currency, titleCase } from '../../utils/format';

/**
 * Special-day greetings on the web.
 *
 * Same three things as the app: what the town is celebrating today, the
 * customer's own greetings with what each is waiting on, and the composer.
 * The greeting is saved before it is paid for, so a failed payment leaves
 * something to retry instead of losing what was written.
 */
const OCCASIONS = [
  { value: 'birthday', label: '🎂 Birthday' },
  { value: 'anniversary', label: '💞 Anniversary' },
  { value: 'festival', label: '🎉 Festival' },
  { value: 'newborn', label: '👶 New baby' },
  { value: 'wedding', label: '💍 Wedding' },
  { value: 'shop_opening', label: '🏪 Shop opening' },
  { value: 'other', label: '✨ Something else' },
];
const EMOJI = Object.fromEntries(OCCASIONS.map((o) => [o.value, o.label.split(' ')[0]]));

const STATUS = {
  awaiting_payment: { label: 'Pay to send', tone: colors.warning },
  in_review: { label: 'With KAAMWALA for review', tone: colors.info },
  approved: { label: 'Approved · goes out on the day', tone: colors.success },
  sent: { label: 'Sent to everyone', tone: colors.success },
  rejected: { label: 'Not approved', tone: colors.danger },
  draft: { label: 'Draft', tone: colors.muted },
};

/*
 * Today and the week after it. A greeting for today is allowed - it goes live
 * the moment KAAMWALA approves it, which the page says plainly.
 */
const dayOptions = () => Array.from({ length: 8 }, (_, i) => {
  const d = new Date(); d.setHours(0, 0, 0, 0); d.setDate(d.getDate() + i);
  return {
    value: d.toISOString(),
    today: i === 0,
    label: i === 0 ? 'Today' : i === 1 ? 'Tomorrow'
      : d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' }),
  };
});

const dayLabel = (value) => {
  const d = new Date(value);
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((new Date(d).setHours(0, 0, 0, 0) - today) / 86400000);
  const date = d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  if (diff === 0) return 'today';
  if (diff === 1) return 'tomorrow';
  return diff < 0 ? date : `${date} · in ${diff} days`;
};

const WishesPage = () => {
  const toast = useToast();
  const days = dayOptions();

  const [tab, setTab] = useState('today');
  const [feed, setFeed] = useState([]);
  const [mine, setMine] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState('');
  const [progress, setProgress] = useState(0);
  const [form, setForm] = useState({
    occasion: 'birthday', forName: '', message: '', image: '', video: '',
    showOn: days[0].value,
  });

  const load = useCallback(async () => {
    try {
      const [f, m, c] = await Promise.all([
        api.wishes.feed(),
        api.wishes.mine().catch(() => ({ data: [] })),
        api.wishes.config().catch(() => ({ data: null })),
      ]);
      setFeed(f.data || []);
      setMine(m.data || []);
      setConfig(c.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const addMedia = async (e, kind) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(kind);
    setProgress(0);
    try {
      // Straight to S3 - no size limit, and the server never touches the bytes.
      const url = await uploadDirect(file, 'wish', setProgress);
      set(kind, url);
    } catch (err) {
      toast.error(err.message || 'Could not upload that file');
    } finally { setUploading(''); e.target.value = ''; }
  };

  const pay = async (wish) => {
    setBusy(true);
    try {
      await startOnlinePayment({ refType: 'wish', refId: wish._id });
      toast.success('Paid. Your greeting is with KAAMWALA for a quick check.');
      setForm({ occasion: 'birthday', forName: '', message: '', image: '', video: '', showOn: days[0].value });
      setTab('mine');
      load();
    } catch (err) {
      if (!err.cancelled) toast.error(err.message || 'The payment could not be completed');
      load();
    } finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (form.message.trim().length < 3) return toast.error('Write a short message');
    setBusy(true);
    try {
      const res = await api.wishes.create({ ...form, message: form.message.trim() });
      // Admin can switch charging off; the server then files it for review directly.
      if (res.data.status !== 'awaiting_payment') {
        toast.success('Your greeting is with KAAMWALA for a quick check.');
        setForm({ occasion: 'birthday', forName: '', message: '', image: '', video: '', showOn: days[0].value });
        setTab('mine');
        setBusy(false);
        load();
        return;
      }
      await pay(res.data);
    } catch (err) {
      toast.error(err.message || 'Could not save the greeting');
      setBusy(false);
    }
    return undefined;
  };

  if (loading) return <Loading text="Loading greetings…" />;

  const price = config?.price ?? 0;
  const off = config?.enabled === false;

  return (
    <div style={{ maxWidth: 720 }}>
      <PageHeader
        title="Special days"
        subtitle="Wish the whole town on their day"
      />

      <div className="flex" style={{ gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <Chip label="🎈 Today" active={tab === 'today'} onClick={() => setTab('today')} />
        <Chip label="📬 My greetings" active={tab === 'mine'} onClick={() => setTab('mine')} />
        <Chip label="✨ Send one" active={tab === 'send'} onClick={() => setTab('send')} />
      </div>

      {tab === 'today' ? (
        !feed.length ? (
          <Empty
            emoji="🎈"
            title="No greetings today"
            text="Birthday and festival wishes from people near you show up here."
          />
        ) : feed.map((w, i) => (
          <Reveal key={w._id} delay={stagger(i, 40)}>
            <div className="card sh-sm" style={{ marginBottom: 12 }}>
              <div className="flex" style={{ gap: 12, alignItems: 'center' }}>
                <span style={{ fontSize: 22 }}>{EMOJI[w.occasion] || '✨'}</span>
                <div className="grow">
                  <div className="t-body" style={{ fontWeight: 700 }}>
                    {titleCase(String(w.occasion).replace('_', ' '))}
                    {w.forName ? ` · ${w.forName}` : ''}
                  </div>
                  <div className="t-tiny">
                    from {w.user?.name || 'someone on KAAMWALA'}
                    {w.user?.city ? ` · ${w.user.city}` : ''}
                  </div>
                </div>
              </div>
              {w.image ? (
                <img
                  src={fileUrl(w.image)}
                  alt=""
                  style={{ width: '100%', maxHeight: 300, objectFit: 'cover', borderRadius: 12, marginTop: 12 }}
                />
              ) : null}
              {w.video ? (
                <video
                  src={fileUrl(w.video)}
                  controls
                  style={{ width: '100%', maxHeight: 340, borderRadius: 12, marginTop: 12, background: '#000' }}
                />
              ) : null}
              <p className="t-body" style={{ marginTop: 12, lineHeight: 1.55 }}>{w.message}</p>
            </div>
          </Reveal>
        ))
      ) : null}

      {tab === 'mine' ? (
        !mine.length ? (
          <Empty
            emoji="📬"
            title="You have not sent one yet"
            text="Send a birthday or festival wish to everyone on KAAMWALA."
            actionLabel="Send a greeting"
            onAction={() => setTab('send')}
          />
        ) : mine.map((w, i) => {
          const s = STATUS[w.status] || STATUS.draft;
          return (
            <Reveal key={w._id} delay={stagger(i, 40)}>
              <div className="card sh-sm" style={{ marginBottom: 12 }}>
                <div className="flex" style={{ gap: 12, alignItems: 'center' }}>
                  <span style={{ fontSize: 22 }}>{EMOJI[w.occasion] || '✨'}</span>
                  <div className="grow">
                    <div className="t-body" style={{ fontWeight: 700 }}>
                      {titleCase(String(w.occasion).replace('_', ' '))}
                      {w.forName ? ` · ${w.forName}` : ''}
                    </div>
                    <div className="t-tiny">Goes out {dayLabel(w.showOn)}</div>
                  </div>
                  <span className="t-tiny" style={{ color: s.tone, fontWeight: 700, textAlign: 'right' }}>
                    {s.label}
                  </span>
                </div>
                <p className="t-body" style={{ marginTop: 10 }}>{w.message}</p>
                {w.status === 'rejected' && w.rejectionReason ? (
                  <div className="t-tiny" style={{ color: colors.danger, marginTop: 6 }}>{w.rejectionReason}</div>
                ) : null}
                {w.status === 'awaiting_payment' ? (
                  <Button
                    title={`Pay ${currency(w.price)} and send`}
                    icon="💳"
                    loading={busy}
                    onClick={() => pay(w)}
                    style={{ marginTop: 12 }}
                  />
                ) : null}
              </div>
            </Reveal>
          );
        })
      ) : null}

      {tab === 'send' ? (
        <form className="card sh-sm" onSubmit={submit}>
          <div className="t-tiny" style={{ letterSpacing: 1, marginBottom: 8 }}>WHAT IS THE OCCASION?</div>
          <div className="flex" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {OCCASIONS.map((o) => (
              <Chip
                key={o.value}
                label={o.label}
                active={form.occasion === o.value}
                onClick={() => set('occasion', o.value)}
              />
            ))}
          </div>

          <Input
            label="Who is it for?"
            icon="🎁"
            placeholder="e.g. Papa, Simran, Sharma ji"
            value={form.forName}
            onChange={(e) => set('forName', e.target.value)}
          />

          <label className="t-tiny" style={{ letterSpacing: 1, display: 'block', margin: '12px 0 6px' }}>
            YOUR MESSAGE
          </label>
          <textarea
            className="input"
            rows={4}
            maxLength={600}
            placeholder="Happy birthday Papa! Wishing you a long and healthy life."
            value={form.message}
            onChange={(e) => set('message', e.target.value)}
            style={{ resize: 'vertical' }}
          />
          <div className="t-tiny" style={{ textAlign: 'right' }}>{form.message.length}/600</div>

          <div className="t-tiny" style={{ letterSpacing: 1, margin: '12px 0 8px' }}>WHICH DAY?</div>
          <div className="flex" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
            {days.map((d) => {
              // The server says which days have hit admin's daily limit.
              const slot = ((config && config.days) || []).find((x) => new Date(x.date).toDateString() === new Date(d.value).toDateString());
              const full = Boolean(slot && slot.full);
              return (
                <Chip
                  key={d.value}
                  label={full ? `${d.label} · Full` : d.label}
                  active={!full && new Date(form.showOn).toDateString() === new Date(d.value).toDateString()}
                  onClick={() => (full ? toast.error('Every greeting slot for that day is taken. Please choose another day.', 'That day is full') : set('showOn', d.value))}
                  style={full ? { opacity: 0.45, cursor: 'not-allowed' } : undefined}
                />
              );
            })}
          </div>
          {days[0] && new Date(form.showOn).toDateString() === new Date(days[0].value).toDateString() ? (
            <div className="t-tiny" style={{ marginTop: -4, marginBottom: 12 }}>
              A greeting for today goes live as soon as KAAMWALA approves it - usually within a few hours.
            </div>
          ) : null}

          <div className="t-tiny" style={{ letterSpacing: 1, marginBottom: 8 }}>PHOTO OR VIDEO (OPTIONAL)</div>
          <div className="flex" style={{ gap: 12, flexWrap: 'wrap' }}>
            <label className="btn ghost" style={{ cursor: 'pointer' }}>
              {uploading === 'image' ? `Uploading ${Math.round(progress * 100)}%` : form.image ? '🖼️ Photo added' : '🖼️ Add photo'}
              <input type="file" accept="image/*" hidden onChange={(e) => addMedia(e, 'image')} />
            </label>
            <label className="btn ghost" style={{ cursor: 'pointer' }}>
              {uploading === 'video' ? `Uploading ${Math.round(progress * 100)}%` : form.video ? '🎬 Video added' : '🎬 Add video'}
              <input type="file" accept="video/*" hidden onChange={(e) => addMedia(e, 'video')} />
            </label>
          </div>
          {form.image ? (
            <img src={fileUrl(form.image)} alt="" style={{ width: 140, borderRadius: 10, marginTop: 12 }} />
          ) : null}

          <div className="t-tiny" style={{ marginTop: 16 }}>
            KAAMWALA checks every greeting before it goes out to everyone.
          </div>
          <Button
            title={off ? 'Greetings are paused' : (config && config.paymentRequired === false) || !price ? 'Send greeting' : `Pay ${currency(price)} and send`}
            icon="💳"
            full
            loading={busy}
            disabled={busy || off || Boolean(uploading)}
            style={{ marginTop: 8 }}
            onClick={submit}
          />
        </form>
      ) : null}
    </div>
  );
};

export default WishesPage;
